import React, { useContext, useEffect, useState } from 'react'
import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Grid, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaChangeApportionmentMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentApportionmentInfoLazyQuery,
  useUpdateFaChangeApportionmentMutation,
  useValidateApportionmentChangeLazyQuery,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'
import { DepartmentApportionmentGrid } from './DepartmentApportmentGrid'
import { DepartmentApportionmentDialog } from './DepartmentApportionmentDialog'

interface DepartmentAllocation {
  department_id: string
  department_name: string
  department_code: string
  location_id: string
  location_name: string
  old_percentage: number
  new_percentage: number
  old_quantity: number
  new_quantity: number
  source_document_id: string
  source_document_type: string
}

export const ChangeApportionmentForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errMsg, setErrMsg] = useState<string>('')
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [allocations, setAllocations] = useState<DepartmentAllocation[]>([])
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [validationWarnings, setValidationWarnings] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  const [openApportionmentDialog, setOpenApportionmentDialog] = useState(false)
  const [apportionmentDialogMode, setApportionmentDialogMode] = useState<
    'add' | 'edit'
  >('add')
  const [editingApportionmentIndex, setEditingApportionmentIndex] = useState<
    number | undefined
  >()
  const [editingApportionmentData, setEditingApportionmentData] = useState<
    DepartmentAllocation | undefined
  >()

  let form, mode
  switch (formMode) {
    case 'add':
      form = 'New'
      mode = 'add'
      break
    case 'edit':
      form = 'Draft'
      mode = 'edit'
      break
    case 'approve-reject':
      form = 'Approve'
      mode = 'approve-reject'
      break
    case 'resubmit':
      form = 'Resubmit'
      mode = 'edit'
      break
    default:
      form = 'New'
      mode = 'add'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const changeApportionmentSchema = yup.object().shape({
    DocNo: yup.mixed().required('Asset is required'),
    change_date: yup.date().required('Change Date is required'),
    reason: yup.string().required('Reason is required'),
  })

  const {
    register,
    setValue,
    control,
    handleSubmit,
    watch,
    errors,
    formState: { isDirty },
  } = useForm<any>({
    defaultValues: {
      DocNo: editData?.Asset?.DocNo || null,
      change_date: editData?.change_date || new Date(),
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(changeApportionmentSchema),
  })

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: assetLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      setErrMsg(message?.substring(15))
      setErrorDia(true)
    },
  })

  const [
    getCurrentApportionmentInfo,
    { loading: apportionmentInfoLoading, data: apportionmentInfoData },
  ] = useGetCurrentApportionmentInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      setErrMsg(message?.substring(15))
      setErrorDia(true)
    },
    onCompleted: ({ getCurrentApportionmentInfo }) => {
      if (
        getCurrentApportionmentInfo?.departments &&
        mode === 'add' &&
        !editData
      ) {
        const initialAllocations = getCurrentApportionmentInfo.departments.map(
          dept => ({
            department_id: dept.department_id,
            department_name: dept.department_name,
            department_code: dept.department_code,
            location_id: dept.location_id || '',
            location_name: dept.location_name || '',
            old_percentage: dept.percentage,
            new_percentage: dept.percentage,
            old_quantity: dept.quantity,
            new_quantity: dept.quantity,
            source_document_id: dept.fa_used_department_id,
            source_document_type: dept.source_document_type || 'REGISTRATION',
          })
        )
        setAllocations(initialAllocations)
      }
    },
  })

  const [
    validateChange,
    { loading: validatingChange },
  ] = useValidateApportionmentChangeLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ validateApportionmentChange }) => {
      setValidationErrors(validateApportionmentChange?.errors || [])
      setValidationWarnings(validateApportionmentChange?.warnings || [])
    },
  })

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createApportionment,
    { loading: createLoading },
  ] = useCreateFaChangeApportionmentMutation({
    onError: ({ message }) => {
      setErrMsg(message?.substring(15))
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
      )
    },
  })

  const [
    updateApportionment,
    { loading: updateLoading },
  ] = useUpdateFaChangeApportionmentMutation({
    onError: ({ message }) => {
      setErrMsg(message?.substring(15))
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
      )
    },
  })

  /* -------------------------------------------- */
  /*                   EFFECTS                    */
  /* -------------------------------------------- */
  // Initialize edit data first
  useEffect(() => {
    if (editData && !isInitialized && getAssetRegistration?.length > 0) {
      // Set selected asset from edit data
      const asset = getAssetRegistration.find(
        a => a?.fa_asset_id === editData?.fa_asset_id
      )

      if (asset) {
        setSelectedAsset(asset)
        setValue('DocNo', asset.DocNo, { shouldValidate: true })
      }

      // Load edit data allocations
      if (editData?.Details && mode === 'edit') {
        const editAllocations = editData.Details.map((d: any) => ({
          department_id: d.department_id,
          department_name: d.Department?.Name || '',
          department_code: d.Department?.Code || '',
          location_id: d.location_id || '',
          location_name: d.Location?.name || '',
          old_percentage: d.old_percentage,
          new_percentage: d.new_percentage,
          old_quantity: d.old_quantity || 0,
          new_quantity: d.new_quantity || 0,
          source_document_id: d.source_document_id,
          source_document_type: d.source_document_type,
        }))
        setAllocations(editAllocations)
      }

      setIsInitialized(true)
    }
  }, [editData, getAssetRegistration, isInitialized, mode])

  useEffect(() => {
    if (selectedAsset?.fa_asset_id && mode === 'add') {
      getCurrentApportionmentInfo({
        variables: {
          fa_asset_id: selectedAsset.fa_asset_id,
          exclude_apportionment_id: undefined,
        },
      })
    }
  }, [selectedAsset, mode])

  /* -------------------------------------------- */
  /*                   HANDLERS                   */
  /* -------------------------------------------- */
  const handleAssetChange = (asset: any) => {
    setSelectedAsset(asset)
    setAllocations([])
    if (asset) {
      setValue('DocNo', asset.DocNo, { shouldValidate: true })
    } else {
      setValue('DocNo', null, { shouldValidate: true })
    }
  }

  const getTotalQuantity = (): number => {
    return (
      apportionmentInfoData?.getCurrentApportionmentInfo?.total_quantity ||
      editData?.Asset?.total_quantity ||
      selectedAsset?.quantity ||
      0
    )
  }

  const recalculateQuantities = (
    allocs: DepartmentAllocation[]
  ): DepartmentAllocation[] => {
    const totalQty = getTotalQuantity()
    return allocs.map(alloc => ({
      ...alloc,
      new_quantity: (alloc.new_percentage / 100) * totalQty,
    }))
  }

  const runValidation = (allocs: DepartmentAllocation[]) => {
    if (selectedAsset?.fa_asset_id && allocs.length > 0) {
      validateChange({
        variables: {
          fa_asset_id: selectedAsset.fa_asset_id,
          details: JSON.stringify(allocs),
        },
      })
    }
  }

  const handleAddApportionmentClick = () => {
    setApportionmentDialogMode('add')
    setEditingApportionmentIndex(undefined)
    setEditingApportionmentData(undefined)
    setOpenApportionmentDialog(true)
  }

  const handleEditApportionmentClick = (
    index: number,
    allocation: DepartmentAllocation
  ) => {
    setApportionmentDialogMode('edit')
    setEditingApportionmentIndex(index)
    setEditingApportionmentData(allocation)
    setOpenApportionmentDialog(true)
  }

  const handleDeleteApportionmentClick = (index: number) => {
    const updatedAllocations = allocations.filter((_, i) => i !== index)
    setAllocations(recalculateQuantities(updatedAllocations))
    runValidation(updatedAllocations)
  }

  const handleSaveApportionment = (
    allocation: DepartmentAllocation,
    dialogMode: 'add' | 'edit',
    index?: number
  ) => {
    let updatedAllocations: DepartmentAllocation[]

    if (dialogMode === 'add') {
      updatedAllocations = [...allocations, allocation]
    } else if (dialogMode === 'edit' && index !== undefined) {
      updatedAllocations = [...allocations]
      updatedAllocations[index] = allocation
    } else {
      return
    }

    updatedAllocations = recalculateQuantities(updatedAllocations)
    setAllocations(updatedAllocations)
    runValidation(updatedAllocations)
    setOpenApportionmentDialog(false)
  }

  const handleSaveAndNewApportionment = (allocation: DepartmentAllocation) => {
    const updatedAllocations = recalculateQuantities([
      ...allocations,
      allocation,
    ])
    setAllocations(updatedAllocations)
    runValidation(updatedAllocations)
  }

  const getTotalNewPercentage = () => {
    return allocations.reduce((sum, a) => sum + (a.new_percentage || 0), 0)
  }

  const validateUniqueDepartmentLocationCombinations = (): {
    isValid: boolean
    errors: string[]
  } => {
    const errors: string[] = []
    const combinations = new Map<string, number>()

    allocations.forEach((alloc, index) => {
      const deptId = alloc.department_id || ''
      const locId = alloc.location_id || ''
      const key = `${deptId}|${locId}`

      if (combinations.has(key)) {
        const firstIndex = combinations.get(key)!
        const deptName =
          alloc.department_code || alloc.department_name || 'Unknown'
        const locName = alloc.location_name || 'No Location'
        errors.push(
          `Duplicate: "${deptName}" + "${locName}" appears at rows ${firstIndex +
            1} and ${index +
            1}. Each Department + Location combination must be unique.`
        )
      } else {
        combinations.set(key, index)
      }
    })

    return { isValid: errors.length === 0, errors }
  }

  const validateRequiredFields = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = []

    allocations.forEach((alloc, index) => {
      if (!alloc.department_id || alloc.department_id.trim() === '') {
        errors.push(`Row ${index + 1}: Department is required`)
      }
      if (alloc.new_percentage < 0) {
        errors.push(`Row ${index + 1}: Percentage cannot be negative`)
      }
      if (alloc.new_percentage > 100) {
        errors.push(`Row ${index + 1}: Percentage cannot exceed 100%`)
      }
    })

    return { isValid: errors.length === 0, errors }
  }

  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (allocations.length === 0) {
      setErrMsg('At least one department apportionment is required.')
      setErrorDia(true)
      return
    }

    const uniqueValidation = validateUniqueDepartmentLocationCombinations()
    if (!uniqueValidation.isValid) {
      setErrMsg(uniqueValidation.errors.join('\n'))
      setErrorDia(true)
      return
    }

    const requiredFieldsValidation = validateRequiredFields()
    if (!requiredFieldsValidation.isValid) {
      setErrMsg(requiredFieldsValidation.errors.join('\n'))
      setErrorDia(true)
      return
    }

    // Validate total percentage
    const totalPercentage = getTotalNewPercentage()
    if (Math.abs(totalPercentage - 100) > 0.01) {
      setErrMsg(
        `Total apportionment must equal 100%. Current total: ${totalPercentage.toFixed(
          2
        )}%`
      )
      setErrorDia(true)
      return
    }

    const detailsInput = allocations.map(a => ({
      source_document_id: a.source_document_id,
      source_document_type: a.source_document_type,
      department_id: a.department_id,
      location_id: a.location_id || null,
      old_percentage: a.old_percentage,
      new_percentage: a.new_percentage,
      old_quantity: a.old_quantity,
      new_quantity: a.new_quantity,
    }))

    const input = {
      apportionment_id: editData?.apportionment_id || undefined,
      fa_asset_id: selectedAsset?.fa_asset_id,
      company_id: CompanyID,
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
      Details: detailsInput,
    }

    if (mode === 'add') {
      createApportionment({ variables: { input } })
    } else {
      updateApportionment({ variables: { input } })
    }
  }

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const rejectFooterOption = {
    name: 'Save',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (editData?.mode === 'resubmit' || formMode === 'resubmit') {
    footerOptions = [rejectFooterOption, submitFooterOption]
  } else {
    footerOptions = [draftFooterOption, submitFooterOption]
  }

  const isLoading =
    assetLoading ||
    apportionmentInfoLoading ||
    createLoading ||
    updateLoading ||
    validatingChange

  return (
    <>
      {isLoading && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          if (isDirty || allocations.length > 0) {
            setOpenExitConf(true)
          } else {
            history.push(
              `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
            )
          }
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Change Apportionment (%)', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'approve-reject'
                ? 'Approve/Reject'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: {
              title: 'Asset Information',
            },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="change_date"
              label="Change Date"
              control={control}
              onChange={(date: Date | null) => {
                setValue('change_date', date)
                return date
              }}
              format="dd/MM/yyyy"
              value={watch('change_date')}
              margin="normal"
              allowKeyboardControl
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              showTodayButton
              disabled={formMode === 'approve-reject'}
              helperText={errors?.change_date?.message}
              error={!!errors?.change_date}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={
                    getAssetRegistration?.sort((a, b) =>
                      (a?.DocNo || '').localeCompare(b?.DocNo || '')
                    ) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo || ''} - ${option?.name || ''}`
                  }
                  fullWidth
                  onChange={(event, value: any) => {
                    handleAssetChange(value)
                    onChange(value?.DocNo || null)
                  }}
                  value={selectedAsset}
                  disabled={formMode === 'approve-reject'}
                  renderOption={(props, option) => (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.DocNo}</span>
                        <span className="desc" style={{ marginLeft: '8px' }}>
                          {props?.name}
                        </span>
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Asset"
                      style={{ width: '100%' }}
                      required
                      margin="normal"
                      helperText={errors?.DocNo?.message}
                      error={!!errors?.DocNo}
                    />
                  )}
                />
              )
            }}
            name="DocNo"
            control={control}
          />

          {selectedAsset && (
            <>
              <TextField
                label="Asset Name"
                value={selectedAsset?.name || ''}
                fullWidth
                margin="dense"
                InputProps={{ disableUnderline: true, readOnly: true }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Total Quantity"
                    value={getTotalQuantity()}
                    fullWidth
                    margin="dense"
                    InputProps={{ disableUnderline: true, readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Original Amount"
                    value={(
                      apportionmentInfoData?.getCurrentApportionmentInfo
                        ?.base_original_amount ||
                      editData?.Asset?.base_original_amount ||
                      0
                    ).toLocaleString()}
                    fullWidth
                    margin="dense"
                    InputProps={{ disableUnderline: true, readOnly: true }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              ref: register,
              autoComplete: 'off',
              helperText: errors?.reason?.message,
              error: !!errors?.reason,
            }}
            required
            disabled={formMode === 'approve-reject'}
          />

          <Controller
            as={TextField}
            name="description"
            label="Description"
            control={control}
            fullWidth
            margin="dense"
            multiline
            defaultValue={editData?.description || ''}
            disabled={formMode === 'approve-reject'}
          />
        </CardContents>

        {/* Department Allocation */}
        <DepartmentApportionmentGrid
          allocations={allocations}
          onAddClick={handleAddApportionmentClick}
          onEditClick={handleEditApportionmentClick}
          onDeleteClick={handleDeleteApportionmentClick}
          getTotalNewPercentage={getTotalNewPercentage}
          validationErrors={validationErrors}
          validationWarnings={validationWarnings}
          disabled={formMode === 'approve-reject' || !selectedAsset}
        />
      </ContentWrapper>

      {/* Department Apportionment Dialog */}
      {openApportionmentDialog && (
        <DepartmentApportionmentDialog
          open={openApportionmentDialog}
          mode={apportionmentDialogMode}
          editIndex={editingApportionmentIndex}
          editData={editingApportionmentData}
          allocations={allocations}
          totalQuantity={getTotalQuantity()}
          CompanyID={CompanyID}
          onClose={() => setOpenApportionmentDialog(false)}
          onSave={handleSaveApportionment}
          onSaveAndNew={handleSaveAndNewApportionment}
          disabled={formMode === 'approve-reject'}
        />
      )}

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
          )
        }}
      />
    </>
  )
}
